import { useMemo, useState } from "react";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  PaginationState,
  VisibilityState,
  FilterFn,
} from "@tanstack/react-table";
import { rankItem } from "@tanstack/match-sorter-utils";
import type { UseDataTableOptions, UseDataTableReturn } from "../dataTable.types";

// Extend TanStack table types for fuzzy filtering
declare module "@tanstack/react-table" {
  interface FilterFns {
    fuzzy: FilterFn<unknown>;
  }
  interface FilterMeta {
    itemRank: ReturnType<typeof rankItem>;
  }
}

/**
 * Fuzzy filter function using match-sorter-utils
 */
const fuzzyFilter: FilterFn<unknown> = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value);
  addMeta({ itemRank });
  return itemRank.passed;
};

/**
 * Hook that encapsulates TanStack React Table setup with pagination,
 * filtering, sorting, and global search functionality.
 *
 * @example
 * const { table, globalFilter, setGlobalFilter } = useDataTable({
 *   data: users,
 *   columns: userColumns,
 *   initialPageSize: 20,
 * });
 */
export function useDataTable<T>({
  data,
  columns,
  initialPageSize = 10,
  initialColumnVisibility = {},
  columnFilters = [],
  onColumnFiltersChange = () => {},
}: UseDataTableOptions<T>): UseDataTableReturn<T> {
  const initialPagination = useMemo(
    () => ({
      pageIndex: 0,
      pageSize: initialPageSize,
    }),
    [initialPageSize]
  );

  const memoizedColumnFilters = useMemo(
    () => columnFilters,
    [JSON.stringify(columnFilters)]
  );

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    initialColumnVisibility
  );

  const [pagination, setPagination] = useState<PaginationState>(initialPagination);

  const [globalFilter, setGlobalFilter] = useState<string>("");

  const table = useReactTable({
    data,
    columns,
    state: {
      pagination,
      columnFilters: memoizedColumnFilters,
      columnVisibility,
      globalFilter,
    },
    onColumnFiltersChange,
    onPaginationChange: setPagination,
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: "fuzzy",
    filterFns: { fuzzy: fuzzyFilter },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return {
    table,
    globalFilter,
    setGlobalFilter,
    pagination,
    columnVisibility,
  };
}

export default useDataTable;

